import ComplianceFooter from "@/components/landing/ComplianceFooter";
import MarketingHeader from "@/components/landing/MarketingHeader";
import { useTheme } from "@/context/ThemeProvider";
import { BRAND, BRAND_RED, BRAND_10, paletteClasses } from "@/lib/landing-theme";
import StructuredData from "@/components/seo/StructuredData";
import { ABOUT_PAGE_JSON_LD, buildBreadcrumbsJsonLd } from "@/components/seo/schema";
import { BookOpen, Target, Sparkles, Mail, Users, MessageCircle } from "lucide-react";
import { ForestCard, MDMonogram } from "@/components/ui/system-primitives";
import { Reveal } from "@/components/Reveal";

/**
 * About — long-form editorial narrative. Layout & typography match the
 * Contact / FAQ reference:
 *   • Red BRAND_RED overline per section
 *   • text-4xl md:text-5xl H1 with green keyword highlight
 *   • text-2xl md:text-3xl H2s with green-outlined icon chip
 *   • text-base md:text-lg body copy on subtleText palette
 */
export default function About() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const p = paletteClasses(isDark);

    return (
        <div className={`min-h-screen font-sans transition-colors duration-300 ${p.shell}`}>
            <StructuredData id="about-page" json={ABOUT_PAGE_JSON_LD} />
            <StructuredData
                id="about-crumbs"
                json={buildBreadcrumbsJsonLd([
                    { name: "Home",  url: "/" },
                    { name: "About", url: "/about" },
                ])}
            />
            <MarketingHeader testidPrefix="about" />

            {/* Hero — matches Contact.jsx hero rhythm exactly */}
            <section className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-8 md:pb-12">
                <div className="flex items-center gap-3 mb-4">
                    <MDMonogram size="md" testid="about-monogram" />
                    <div className="overline" style={{ color: BRAND_RED }} data-testid="about-eyebrow">
                        About Us
                    </div>
                </div>
                <h1
                    className="font-heading text-h1 mb-4"
                    data-testid="about-h1"
                >
                    Our <span style={{ color: BRAND }}>Story</span>.
                </h1>
            </section>

            {/* Story body */}
            <section className="max-w-4xl mx-auto px-6 pb-8 md:pb-12">
                <div className="space-y-5" data-testid="about-story">
                    <P p={p}>
                        In 2010, in <Kw>Hong Kong</Kw>, our founder started noticing something. Big
                        companies and big money were shaping how everyone else traded — from large
                        firms right down to everyday people. That curiosity grew into a bigger
                        question: not just what happens in the markets, but
                        <Kw> why people trade the way they do</Kw>.
                    </P>
                    <P p={p}>
                        Over the next few years, that question kept growing. Years of studying how
                        people make money decisions, real work in finance across Asia, and watching
                        African markets grow all led to one simple idea — there were plenty of
                        tools to study markets, but almost none to help people understand their own
                        habits, especially in <Kw>African markets</Kw> that were too often left out
                        of the conversation entirely.
                    </P>
                    <P p={p}>
                        Melian Dialogue was built to close that gap. Today, we focus only on
                        African shares, currencies, and markets — bringing real prices, a fun
                        practice simulator, and a smart AI helper together in one place, so a new
                        generation of African traders can build skill and confidence
                        <Kw> before they risk a single real coin</Kw>.
                    </P>
                </div>
            </section>

            {/* Mission */}
            <Reveal>
                <SectionHeading icon={Target}   eyebrow="Our Mission"       title="What we're here to do."             testid="mission" />
                <section className="max-w-4xl mx-auto px-6 pb-8 md:pb-12">
                    <ForestCard testid="about-mission-callout">
                        <p className="font-body text-body-lg">
                            To help every aspiring trader in Africa learn to trade well &mdash; using
                            real market prices, zero real risk, and AI that helps you understand not
                            just the markets, but yourself.
                        </p>
                    </ForestCard>
                </section>
            </Reveal>

            {/* What We Do */}
            <Reveal>
                <SectionHeading icon={Sparkles} eyebrow="What We Do"         title="A safe place to practice trading, built for Africa." testid="what" />
                <section className="max-w-4xl mx-auto px-6 pb-8 md:pb-12">
                    <div className="space-y-5" data-testid="about-what">
                        <P p={p}>
                            Melian Dialogue is a game-like, purely educational trading simulator. It
                            uses real, live prices from African stock exchanges — but you only ever
                            trade with <Kw>virtual money</Kw>. As you practice, our own Behavioral
                            Coefficient (BeCo) Index™ gives you two simple scores: <Kw>Alpha (α)</Kw>,
                            how disciplined your trading is, and <Kw>Sigma (σ)</Kw>, how much risk
                            you're taking — turning every practice trade into a lesson in better
                            decisions.
                        </P>
                        <P p={p}>
                            We are <Kw>not a brokerage</Kw>. We never touch real money, real deposits,
                            or real trades. We are, first and foremost, a place to learn and build
                            skill — built for the market Africa deserves.
                        </P>
                    </div>
                </section>
            </Reveal>

            {/* Media */}
            <Reveal>
                <SectionHeading icon={Mail}          eyebrow="Media Contact"    title="For press, interviews, and partnerships." testid="media" />
                <section className="max-w-4xl mx-auto px-6 pb-8 md:pb-12">
                    <P p={p}>
                        For press questions, interviews, or partnerships, email us at <MailLink />.
                        We&rsquo;re happy to share more about our story, our technology, or our plans
                        for financial education in Africa.
                    </P>
                </section>
            </Reveal>

            {/* Careers */}
            <Reveal>
                <SectionHeading icon={Users}         eyebrow="Working With Us"  title="We're always hiring believers."           testid="careers" />
                <section className="max-w-4xl mx-auto px-6 pb-8 md:pb-12">
                    <div className="space-y-5" data-testid="about-careers">
                        <P p={p}>
                            We&rsquo;re building something new that brings together AI, human behavior,
                            and <Kw>African markets</Kw> — and we&rsquo;re always glad to hear from
                            people who want to help. Whether you&rsquo;re a developer, designer,
                            researcher, or someone who simply believes in what we&rsquo;re building,
                            we&rsquo;d love to hear from you.
                        </P>
                        <P p={p}>
                            To express interest in working with us, email <MailLink /> with a short note
                            about yourself and what you&rsquo;d bring to the team.
                        </P>
                    </div>
                </section>
            </Reveal>

            {/* Enquiries */}
            <Reveal>
                <SectionHeading icon={MessageCircle} eyebrow="General Enquiries" title="Anything else on your mind?"             testid="enquiries" />
                <section className="max-w-4xl mx-auto px-6 pb-16 md:pb-24">
                    <P p={p}>
                        Questions about the platform, partnerships, or anything else? Reach us anytime
                        at <MailLink />.
                    </P>
                </section>
            </Reveal>

            <ComplianceFooter />
        </div>
    );
}

/* ── shared editorial primitives ────────────────────────── */

function SectionHeading({ icon: Icon, eyebrow, title, testid }) {
    return (
        <section className="max-w-4xl mx-auto px-6 pt-8 md:pt-12" data-testid={`about-${testid}-heading`}>
            <div className="flex items-center gap-3 mb-3">
                <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: BRAND_10, color: BRAND }}
                    aria-hidden="true"
                >
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                </span>
                <div className="overline" style={{ color: BRAND_RED }}>
                    {eyebrow}
                </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight leading-[1.15] mb-2">
                {title}
            </h2>
        </section>
    );
}

function P({ children, p }) {
    return (
        <p className={`text-base md:text-lg leading-relaxed ${p.subtleText}`}>
            {children}
        </p>
    );
}

// Green keyword highlight — matches the `<span style={{color: BRAND}}>` treatment on Contact/FAQ H1s.
function Kw({ children }) {
    return <span className="font-semibold" style={{ color: BRAND }}>{children}</span>;
}

function MailLink() {
    return (
        <a
            href="mailto:hello@meliandialogue.com"
            className="font-semibold underline underline-offset-2 transition-colors"
            style={{ color: BRAND }}
            data-testid="about-mail-link"
        >
            hello@meliandialogue.com
        </a>
    );
}
